import {
  DEALER_SSO_SELECTED_LENDER_PATH,
  IS_SSO_AND_SHOW_LOAN_VARIABLES_DIALOG,
} from 'modules/auth/constants/auth.const';
import { IUser } from 'modules/core/types';
import { SSO_TYPE } from 'modules/core/constants';
import { IMenuLink } from 'modules/core/components/SideBar/sideBarMenuLinks';
import { getMakeItWorkForAllLendersLinks, getSelectableLenderLinks } from 'modules/core/components/SideBar/utils';
import { ROUTER_PATH } from 'router/const/routerPath.const';

const DEALER_SSO_NON_MODAL_NAVIGATION_PATHS: readonly string[] = [
  ROUTER_PATH.RAC_AFFORDABILITY_ENGINE,
  ROUTER_PATH.PROBABILITY_ESTIMATOR,
  ROUTER_PATH.APPROVED_LOAN_INPUT,
  ROUTER_PATH.CREDIT_INTERVIEW,
];

export const isDealerSsoNonModalNavigationPath = (pathname: string): boolean =>
  DEALER_SSO_NON_MODAL_NAVIGATION_PATHS.includes(pathname);

export const getDealerSsoSidebarLenderPaths = (lenderLinks: IMenuLink[]): string[] =>
  lenderLinks.map((link) => link.link).filter((link): link is string => Boolean(link?.includes('/lenders/')));

export const isDealerSsoSidebarLenderPath = (pathname: string, lenderLinks: IMenuLink[]): boolean =>
  getDealerSsoSidebarLenderPaths(lenderLinks).includes(pathname);

export const getDealerSsoSelectedLenderPath = (): string | null =>
  sessionStorage.getItem(DEALER_SSO_SELECTED_LENDER_PATH);

export const saveDealerSsoSelectedLenderPath = (lenderPath: string): void => {
  const currentLenderPath = sessionStorage.getItem(DEALER_SSO_SELECTED_LENDER_PATH);

  if (currentLenderPath && currentLenderPath !== lenderPath) {
    sessionStorage.removeItem(IS_SSO_AND_SHOW_LOAN_VARIABLES_DIALOG);
  }

  sessionStorage.setItem(DEALER_SSO_SELECTED_LENDER_PATH, lenderPath);
};

export const getDealerSsoModalLenderLinks = (
  lenderLinks: IMenuLink[],
  disabledFeaturesList: string[],
  userRole?: IUser['role'],
): IMenuLink[] => [
  ...getSelectableLenderLinks(lenderLinks, disabledFeaturesList, userRole),
  ...getMakeItWorkForAllLendersLinks(lenderLinks, disabledFeaturesList, userRole),
];

export const isDealerSsoLenderSelectionComplete = (
  user: IUser | null,
  lenderLinks: IMenuLink[],
  disabledFeaturesList: string[],
): boolean => {
  if (!user?.is_sso || user?.sso_type !== SSO_TYPE.DEALER) return true;

  const modalLenders = getDealerSsoModalLenderLinks(lenderLinks, disabledFeaturesList, user.role);

  if (modalLenders.length <= 1) return true;

  return Boolean(getDealerSsoSelectedLenderPath());
};

/*
  The goal of that function is to decide if we need to call credit decision API to determine 
  which lender user will be landed on or not. In case of user uses lender selection modal window
  we can avoid calling for credit decision until he chooses any lender manually.
 */
export const getDealerSsoLoginModalLanding = (
  user: IUser | null,
  lenderLinks: IMenuLink[],
  disabledFeaturesList: string[],
): { willShowModal: boolean; landingPath: string | null } => {
  if (!user?.is_sso || user?.sso_type !== SSO_TYPE.DEALER) {
    return { willShowModal: false, landingPath: null };
  }

  const redirectFreeLenders = lenderLinks.filter((lender) => lender.withoutLenderRedirect);
  const modalLenders = getDealerSsoModalLenderLinks(redirectFreeLenders, disabledFeaturesList, user.role);

  if (modalLenders.length === 0) {
    return { willShowModal: false, landingPath: null };
  }

  return { willShowModal: modalLenders.length > 1, landingPath: (modalLenders[0]?.link as string) ?? null };
};
